import "../kendo.html.button.js";
import "../kendo.icons.js";
import "../kendo.speechtotextbutton.js";
import "../kendo.textarea.js";
import "../kendo.textbox.js";
import "../kendo.upload.js";
import { Widget } from "../core/base/widget";
import { inject } from "../core/service-container";
import { HtmlService } from "../core/services/html.service";
import { UtilsService } from "../core/services/utils.service";
import { FileUtilsService } from "../core/services/file-utils.service";
import { DomUtilsService } from "../core/services/dom-utils.service";
import { NS, STYLES, REFERENCES, EVENTS, LINE_MODE, CLICK, FOCUSIN, FOCUSOUT } from "./constants";
import { renderPromptBox, IAffixConfig, IFileSelectButtonConfig, IButtonSettingsConfig, IRenderConfig } from "./templates";
import { IKendoJQueryStatic, IKendoJQuery } from "../core";
import { AccessibilityManager, ActionButtonManager, SpeechHandler, FileHandler, InputManager } from "./collaborators";
import type { IPromptBoxOptions, IPromptBoxMessages, IActionButtonEvent, IFileSelectButtonSettings } from "./models";
import type { IFile } from "../upload";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

const DEFAULT_MESSAGES: IPromptBoxMessages = {
    placeholder: "Type a message...",
    actionButton: "Send",
    actionButtonLoading: "Stop",
    messageBoxTitle: "Message",
    fileSelectButton: "Attach file"
};

/**
 * PromptBox widget - A composite input component for chat-like interfaces.
 *
 * Supports three modes:
 * - "single": Single-line text input
 * - "multi": Multi-line textarea with fixed rows
 * - "auto": Auto-expanding textarea (starts from 1 row)
 */
export class PromptBox extends Widget {
    static readonly options = {
        name: "PromptBox",
        prefix: "",
        mode: LINE_MODE.AUTO,
        placeholder: "",
        value: "",
        enable: true,
        readonly: false,
        title: "",
        fillMode: undefined,
        maxTextAreaHeight: undefined,
        rows: 1,
        headerTemplate: null,
        loading: false,
        actionButton: null,
        fileSelectButton: false,
        speechToTextButton: true,
        startAffixTemplate: null,
        endAffixTemplate: null,
        topAffixTemplate: null,
        messages: DEFAULT_MESSAGES
    };

    events: string[] = [
        EVENTS.valueChange,
        EVENTS.input,
        EVENTS.promptAction,
        EVENTS.fileSelect,
        EVENTS.fileRemove,
        EVENTS.speechToTextClick,
        EVENTS.speechToTextStart,
        EVENTS.speechToTextEnd,
        EVENTS.speechToTextError,
        EVENTS.speechToTextResult,
        EVENTS.focus,
        EVENTS.blur,
        EVENTS.inputFocus,
        EVENTS.inputBlur,
        EVENTS.multilineStateChange
    ];

    private _value: string = "";
    private _loading: boolean = false;
    private _readonly: boolean = false;
    private _hasFocus: boolean = false;
    private _fileInputId: string = "";

    private utilsService: UtilsService;
    private htmlService: HtmlService;
    private fileUtilsService: FileUtilsService;
    private domUtilsService: DomUtilsService;
    private accessibilityManager!: AccessibilityManager;
    private actionButtonManager!: ActionButtonManager;
    private speechHandler!: SpeechHandler;
    private fileHandler!: FileHandler;
    private inputManager!: InputManager;

    /**
     * Constructs a new PromptBox instance.
     */
    constructor(
        element: HTMLElement | IKendoJQuery,
        options?: Partial<IPromptBoxOptions>,
        utilsService = inject(UtilsService),
        htmlService = inject(HtmlService),
        fileUtilsService = inject(FileUtilsService),
        domUtilsService = inject(DomUtilsService)
    ) {
        super(element as any, $.extend(true, {}, PromptBox.options, options));

        this.utilsService = utilsService;
        this.htmlService = htmlService;
        this.fileUtilsService = fileUtilsService;
        this.domUtilsService = domUtilsService;

        this._value = (this.options as IPromptBoxOptions).value || "";
        this._loading = (this.options as IPromptBoxOptions).loading || false;
        this._fileInputId = "promptbox_file_" + this.utilsService.guid();

        this._initWrapper();
        this._initInputManager();
        this._initSpeechHandler();
        this._initFileHandler();
        this._attachEvents();
        this.fileHandler.renderAttachments();
        this._initAccessibilityManager();
        this._initActionButtonManager();

        this.bind(this.events, this.options);

        if (!(this.options as IPromptBoxOptions).enable) {
            this.enable(false);
        }

        if ((this.options as IPromptBoxOptions).readonly) {
            this.readonly(true);
        }

        if (this._value && this._getMode() === LINE_MODE.AUTO) {
            this.inputManager.updateAutoResize();
        }

        this.actionButtonManager.updateState();
    }

    /**
     * Gets or sets the input value.
     */
    value(newValue?: string): string | void {
        if (newValue === undefined) {
            return this._value;
        }

        this._value = newValue;
        this.inputManager.setValue(newValue);
        this.actionButtonManager.updateState();
    }

    /**
     * Enables or disables the PromptBox.
     * When enabling, this also clears the readonly state (similar to TextBox behavior).
     */
    enable(enabled: boolean = true): void {
        const options = this.options as IPromptBoxOptions;
        options.enable = enabled;

        if (enabled) {
            this._readonly = false;
            options.readonly = false;
            this.inputManager.setReadonly(false);
            this.wrapper.removeClass(STYLES.disabled);
        } else {
            this.wrapper.addClass(STYLES.disabled);
        }

        this.inputManager.enable(enabled);
        this.speechHandler.enable(enabled);
        this._updateFileSelectButtonState();
        this.actionButtonManager.updateState();

        this.accessibilityManager.updateWrapper({ disabled: !enabled });
        this.accessibilityManager.updateInputAccessibility();
    }

    /**
     * Gets or sets the read-only state of the PromptBox.
     */
    readonly(value?: boolean): boolean | void {
        if (value === undefined) {
            return this._readonly;
        }

        this._readonly = value;
        const options = this.options as IPromptBoxOptions;
        options.readonly = value;
        this.inputManager.setReadonly(value);

        this.accessibilityManager.updateInputAccessibility();

        this.actionButtonManager.updateState();
        this.speechHandler.enable(!value);
        this._updateFileSelectButtonState();
    }

    /**
     * Gets or sets the loading state (shows stop button instead of send).
     */
    loading(value?: boolean): boolean | void {
        if (value === undefined) {
            return this._loading;
        }

        this._loading = value;

        this.accessibilityManager.updateWrapper({ loading: value });
        this.actionButtonManager.setLoading(value);
    }

    /**
     * Focuses the input element.
     */
    focus(): void {
        this.inputManager.focus();
    }

    /**
     * Blurs the input element.
     */
    blur(): void {
        this.inputManager.blur();
    }

    /**
     * Gets or sets the attached files.
     * @param newFiles - Optional array of files to set. If not provided, returns current files.
     * @returns Array of attached files when getting, void when setting.
     */
    files(newFiles?: IFile[]): IFile[] | void {
        if (newFiles === undefined) {
            return this.fileHandler.getFiles();
        }

        this.fileHandler.setFiles(newFiles);
    }

    /**
     * Clears all attached files.
     */
    clearFiles(): void {
        this.fileHandler.clearFiles();
    }

    /**
     * Destroys the widget.
     */
    destroy(): void {
        this._detachEvents();
        this._destroyCollaborators();
        super.destroy();
    }

    private _destroyCollaborators(): void {
        this.fileHandler.destroy();
        this.speechHandler.destroy();
        this.inputManager.destroy();
    }

    /**
     * Updates widget options dynamically.
     * For complex changes (templates, buttons, mode), the widget is rebuilt.
     */
    setOptions(options: Partial<IPromptBoxOptions>): void {
        const requiresRebuild = options.headerTemplate !== undefined ||
            options.startAffixTemplate !== undefined ||
            options.endAffixTemplate !== undefined ||
            options.topAffixTemplate !== undefined ||
            options.actionButton !== undefined ||
            options.actionButtonSettings !== undefined ||
            options.fileSelectButton !== undefined ||
            options.speechToTextButton !== undefined ||
            options.mode !== undefined;

        if (requiresRebuild) {
            const currentValue = this._value;
            const wasLoading = this._loading;
            const attachments = (this.options as IPromptBoxOptions).attachments;
            const currentFiles = this.fileHandler.getFiles();

            this._detachEvents();
            this._destroyCollaborators();

            super.setOptions(options as any);

            // Remove old wrapper completely (element was moved outside wrapper in _initWrapper)
            this.wrapper.remove();

            this._initWrapper();
            this._initInputManager();
            this._initSpeechHandler();
            this._initFileHandler();
            this._attachEvents();

            // Restore files that were attached before the rebuild
            if (currentFiles.length > 0) {
                this.fileHandler.setFiles(currentFiles);
            }

            this._initAccessibilityManager();
            this._initActionButtonManager();

            if (currentValue) {
                this.value(currentValue);
            }
            if (wasLoading) {
                this.loading(true);
            }
            if (attachments) {
                (this.options as IPromptBoxOptions).attachments = attachments;
                this.fileHandler.renderAttachments();
            }
            return;
        }

        super.setOptions(options as any);

        if (options.placeholder !== undefined || options.messages?.placeholder !== undefined) {
            this.inputManager.updatePlaceholder();
        }

        if (options.messages?.actionButton !== undefined || options.messages?.actionButtonLoading !== undefined) {
            this.actionButtonManager.updateMessages();
        }

        if (options.enable !== undefined) {
            this.enable(options.enable);
        }

        if (options.readonly !== undefined) {
            this.readonly(options.readonly);
        }

        if (options.loading !== undefined) {
            this.loading(options.loading);
        }
    }

    private _initInputManager(): void {
        this.inputManager = new InputManager({
            wrapper: this.wrapper,
            getOptions: () => this.options as IPromptBoxOptions,
            getMode: () => this._getMode(),
            callbacks: {
                onInput: () => this._handleInput(),
                onKeyDown: (e) => this._handleKeyDown(e),
                onFocus: () => this.trigger(EVENTS.inputFocus),
                onBlur: () => this.trigger(EVENTS.inputBlur),
                onMultilineStateChange: (isMultiline, wasMultiline) => {
                    this._handleAffixReorganization(isMultiline);
                    this.trigger(EVENTS.multilineStateChange, { isMultiline, wasMultiline });
                }
            }
        });
        this.inputManager.init();
    }

    /**
     * Reorganizes affixes when auto mode switches between single-line and multiline.
     * In multiline: startAffix content moves to endAffix (left side) with a spacer.
     * In single-line: startAffix content returns to its original container.
     */
    private _handleAffixReorganization(isMultiline: boolean): void {
        const options = this.options as IPromptBoxOptions;
        if (!options.startAffixTemplate) {
            return;
        }

        const startAffix = this.wrapper.find(`[${REFERENCES.startAffix}]`);
        const endAffix = this.wrapper.find(`[${REFERENCES.endAffix}]`);

        if (!startAffix.length || !endAffix.length) {
            return;
        }

        if (isMultiline) {
            const startAffixContent = startAffix.contents().detach();
            startAffix.addClass(STYLES.hidden);

            const spacer = $(`<div class="${STYLES.spacer}" ${REFERENCES.affixSpacer}></div>`);

            endAffix.prepend(spacer);
            endAffix.prepend(startAffixContent);
        } else {
            const spacer = endAffix.find(`[${REFERENCES.affixSpacer}]`);
            if (spacer.length) {
                const startAffixContent = spacer.prevAll().detach();

                startAffix.empty().append(startAffixContent);
                startAffix.removeClass(STYLES.hidden);

                spacer.remove();
            }
        }
    }

    private _initSpeechHandler(): void {
        this.speechHandler = new SpeechHandler({
            wrapper: this.wrapper,
            getSpeechToTextButtonOption: () => (this.options as IPromptBoxOptions).speechToTextButton,
            callbacks: {
                onStart: (e) => this.trigger(EVENTS.speechToTextStart, e),
                onEnd: (e) => this.trigger(EVENTS.speechToTextEnd, e),
                onResult: (e) => {
                    const transcript = e.alternatives?.[0]?.transcript || "";
                    if (transcript) {
                        const currentValue = this._value || "";
                        const newValue = currentValue + (currentValue ? " " : "") + transcript;
                        this.value(newValue);
                    }
                    this.trigger(EVENTS.speechToTextResult, { ...e, transcript });
                },
                onError: (e) => this.trigger(EVENTS.speechToTextError, e)
            }
        });
        this.speechHandler.init();
    }

    private _initFileHandler(): void {
        this.fileHandler = new FileHandler({
            wrapper: this.wrapper,
            getOptions: () => this.options as IPromptBoxOptions,
            htmlService: this.htmlService,
            fileUtilsService: this.fileUtilsService,
            utilsService: this.utilsService,
            domUtilsService: this.domUtilsService,
            callbacks: {
                onFilesChanged: () => this.actionButtonManager.updateState(),
                onFileSelect: (files) => this.trigger(EVENTS.fileSelect, { files }),
                onFileRemove: (file, remainingFiles) => this.trigger(EVENTS.fileRemove, { file, files: remainingFiles })
            }
        });
        this.fileHandler.initUpload();
    }

    private _initAccessibilityManager(): void {
        this.accessibilityManager = new AccessibilityManager({
            wrapper: this.wrapper,
            getOptions: () => this.options as IPromptBoxOptions,
            getInputElement: () => this.inputManager.getElement()
        });
        this.accessibilityManager.applyInitial();
    }

    private _initActionButtonManager(): void {
        this.actionButtonManager = new ActionButtonManager({
            wrapper: this.wrapper,
            getMessages: () => this._getMessages(),
            getSettings: () => (this.options as IPromptBoxOptions).actionButton,
            isDisabled: () => {
                const options = this.options as IPromptBoxOptions;
                const widgetDisabled = !options.enable;
                const widgetReadonly = options.readonly;
                const actionButtonDisabled = options.actionButton?.enable === false;
                return widgetDisabled || widgetReadonly || actionButtonDisabled;
            },
            isLoading: () => this._loading,
            hasContent: () => !!this._value?.trim() || this.fileHandler.hasFiles()
        });
    }

    /**
     * Gets the input mode (single, multi, or auto).
     */
    private _getMode(): string {
        const options = this.options as IPromptBoxOptions;
        return options.mode || LINE_MODE.AUTO;
    }

    /**
     * Gets the merged messages, including overrides from button settings.
     */
    private _getMessages(): IPromptBoxMessages {
        const options = this.options as IPromptBoxOptions;
        const actionButtonSettings = options.actionButton;
        const fileSelectButtonSettings = typeof options.fileSelectButton === "object" ? options.fileSelectButton as IFileSelectButtonSettings : null;
        const baseMessages = $.extend({}, DEFAULT_MESSAGES, options.messages);
        if (actionButtonSettings?.text) {
            baseMessages.actionButton = actionButtonSettings.text;
        }
        if (actionButtonSettings?.loadingText) {
            baseMessages.actionButtonLoading = actionButtonSettings.loadingText;
        }
        if (fileSelectButtonSettings?.text) {
            baseMessages.fileSelectButton = fileSelectButtonSettings.text;
        }
        return baseMessages;
    }

    /**
     * Gets whether the action button should be shown. Always returns true.
     */
    private _showActionButton(): boolean {
        return true;
    }

    /**
     * Gets whether the speech-to-text button should be shown.
     */
    private _showSpeechToTextButton(): boolean {
        const options = this.options as IPromptBoxOptions;
        return options.speechToTextButton === true ||
            options.speechToTextButton === null ||
            options.speechToTextButton === undefined ||
            (typeof options.speechToTextButton === "object" && options.speechToTextButton !== null);
    }

    /**
     * Gets whether the file select button should be shown.
     */
    private _showFileSelectButton(): boolean {
        const options = this.options as IPromptBoxOptions;
        return options.fileSelectButton === true ||
            (typeof options.fileSelectButton === "object" && options.fileSelectButton !== null);
    }

    /**
     * Updates the file select button state based on enable/readonly options.
     */
    private _updateFileSelectButtonState(): void {
        const options = this.options as IPromptBoxOptions;
        const fileSelectButton = this.wrapper.find(`[${REFERENCES.fileSelectButton}]`);

        if (!fileSelectButton.length) {
            return;
        }

        const fileSelectSettings = this._getFileSelectButtonSettings();
        const isDisabled = !options.enable || options.readonly || fileSelectSettings?.enable === false;

        if (isDisabled) {
            fileSelectButton.addClass(STYLES.disabled);
            fileSelectButton.attr("aria-disabled", "true");
        } else {
            fileSelectButton.removeClass(STYLES.disabled);
            fileSelectButton.removeAttr("aria-disabled");
        }
    }

    /**
     * Gets the file select button settings.
     */
    private _getFileSelectButtonSettings(): IFileSelectButtonSettings | undefined {
        const options = this.options as IPromptBoxOptions;
        if (typeof options.fileSelectButton === "object" && options.fileSelectButton !== null) {
            return options.fileSelectButton as IFileSelectButtonSettings;
        }
        return undefined;
    }

    /**
     * Creates the wrapper element using wrap instead of replaceWith.
     * This keeps the original element in the DOM so jQuery data binding works.
     */
    private _initWrapper(): void {
        const options = this.options as IPromptBoxOptions;
        const messages = this._getMessages();
        const mode = this._getMode();

        let modeClass = "";
        if (mode === LINE_MODE.SINGLE) {
            modeClass = ` ${STYLES.promptBoxSingleline}`;
        } else if (mode === LINE_MODE.MULTI) {
            modeClass = ` ${STYLES.promptBoxMultiline}`;
        }

        const stylingClasses = this._getAppearanceClasses();

        const affixConfig: IAffixConfig = {
            startAffixTemplate: options.startAffixTemplate,
            endAffixTemplate: options.endAffixTemplate,
            topAffixTemplate: options.topAffixTemplate
        };

        const fileSelectButtonConfig = this._getFileSelectButtonConfig();
        const buttonSettingsConfig = this._getButtonSettingsConfig();

        const renderConfig: IRenderConfig = {
            affixConfig,
            fileSelectButtonConfig,
            buttonSettingsConfig
        };

        const wrapperContent = renderPromptBox(
            options.headerTemplate,
            messages,
            this._showActionButton(),
            mode as any,
            this._showSpeechToTextButton(),
            renderConfig
        );

        this.wrapper = this.element.wrap(`<div class="${STYLES.input}${stylingClasses} ${STYLES.promptBox}${modeClass}" role="group"></div>`).parent() as IKendoJQuery;
        this.wrapper.prepend(wrapperContent);
        this.element.addClass(STYLES.hidden);
        this.wrapper.after(this.element);
    }

    /**
     * Gets appearance CSS classes for fillMode.
     * PromptBox only supports fillMode at the root level per kendo-themes spec.
     */
    private _getAppearanceClasses(): string {
        const options = this.options as IPromptBoxOptions;

        if (options.fillMode) {
            return ` k-input-${options.fillMode}`;
        }

        return "";
    }

    /**
     * Gets the file select button configuration for template rendering.
     */
    private _getFileSelectButtonConfig(): IFileSelectButtonConfig | undefined {
        if (!this._showFileSelectButton()) {
            return undefined;
        }

        const settings = this._getFileSelectButtonSettings();
        let accept = settings?.accept;

        if (!accept && settings?.restrictions?.allowedExtensions?.length) {
            accept = settings.restrictions.allowedExtensions
                .map(ext => ext.startsWith(".") ? ext : `.${ext}`)
                .join(",");
        }

        return {
            enable: settings?.enable,
            fileInputId: this._fileInputId,
            accept: accept,
            multiple: settings?.multiple,
            showSpacer: settings?.showSpacer,
            settings: settings
        };
    }

    /**
     * Gets the button settings configuration for template rendering.
     */
    private _getButtonSettingsConfig(): IButtonSettingsConfig {
        const options = this.options as IPromptBoxOptions;
        const speechOption = options.speechToTextButton;
        const speechSettings = typeof speechOption === "object" && speechOption !== null ? speechOption : undefined;
        return {
            actionButtonSettings: options.actionButton || undefined,
            fileSelectButtonSettings: this._getFileSelectButtonSettings(),
            speechToTextButtonSettings: speechSettings
        };
    }

    /**
     * Gets the current multiline state (for auto mode).
     */
    isMultiline(): boolean {
        return this.inputManager.isMultiline();
    }

    /**
     * Attaches event handlers.
     */
    private _attachEvents(): void {
        this.wrapper.on(CLICK + NS, `[${REFERENCES.sendButton}]`, (e: JQuery.ClickEvent) => {
            e.preventDefault();
            this._handleSend();
        });

        this.wrapper.on(CLICK + NS, `[${REFERENCES.fileSelectButton}]`, (e: JQuery.ClickEvent) => {
            e.preventDefault();
            this.fileHandler.handleFileSelectClick();
        });

        this.wrapper.on(CLICK + NS, `[${REFERENCES.attachmentRemoveButton}]`, (e: JQuery.ClickEvent) => {
            e.preventDefault();
            const target = e.currentTarget as HTMLElement;
            const uid = target.getAttribute("data-uid");
            if (uid) {
                this.fileHandler.removeAttachmentByUid(uid);
            }
        });

        this.wrapper.on(FOCUSIN + NS, (e: JQuery.FocusInEvent) => {
            this._handleFocusIn(e);
        });

        this.wrapper.on(FOCUSOUT + NS, (e: JQuery.FocusOutEvent) => {
            this._handleFocusOut(e);
        });
    }

    /**
     * Detaches event handlers.
     */
    private _detachEvents(): void {
        this.wrapper.off(NS);
    }

    /**
     * Handles focus entering the widget.
     */
    private _handleFocusIn(e: JQuery.FocusInEvent): void {
        if (!this._hasFocus) {
            this._hasFocus = true;
            this.wrapper.addClass(STYLES.focus);
            this.trigger(EVENTS.focus);
        }
    }

    /**
     * Handles focus leaving the widget.
     */
    private _handleFocusOut(e: JQuery.FocusOutEvent): void {
        const relatedTarget = e.relatedTarget as HTMLElement;
        const isInsideWidget = relatedTarget && this.wrapper[0].contains(relatedTarget);

        if (!isInsideWidget && this._hasFocus) {
            this._hasFocus = false;
            this.wrapper.removeClass(STYLES.focus);
            this.trigger(EVENTS.blur);
        }
    }

    /**
     * Handles the send action.
     */
    private _handleSend(): void {
        const options = this.options as IPromptBoxOptions;

        if (!options.enable || options.readonly || options.actionButton?.enable === false) {
            return;
        }

        if (this._loading) {
            const eventData: IActionButtonEvent = { actionType: "stop" };
            this.trigger(EVENTS.promptAction, eventData);
            this.loading(false);
            return;
        }

        const value = this._value;
        const hasValue = value?.trim();
        const hasFiles = this.fileHandler.hasFiles();

        if (!hasValue && !hasFiles) {
            return;
        }

        const eventData: IActionButtonEvent = {
            actionType: "send",
            value: hasValue ? value.trim() : "",
            files: this.fileHandler.getFiles()
        };
        this.trigger(EVENTS.promptAction, eventData);

        this.value("");
        this.clearFiles();
        this.focus();
    }

    /**
     * Handles input changes.
     */
    private _handleInput(): void {
        const oldValue = this._value;
        this._value = this.inputManager.getValue();

        this.actionButtonManager.updateState();

        this.trigger(EVENTS.input, { value: this._value });

        if (oldValue !== this._value) {
            this.trigger(EVENTS.valueChange, {
                oldValue,
                newValue: this._value
            });
        }
    }

    /**
     * Handles keydown events.
     * Enter sends the message in all modes.
     * Shift+Enter adds a newline in multi/auto modes.
     * Enter is ignored when loading to prevent accidental stops.
     */
    private _handleKeyDown(e: JQuery.KeyDownEvent): void {
        const mode = this._getMode();
        const isEnter = e.key === "Enter" || e.which === 13 || e.keyCode === 13;

        if (isEnter) {
            if (e.shiftKey && mode !== LINE_MODE.SINGLE) {
                return;
            }

            e.preventDefault();

            if (!this._loading) {
                this._handleSend();
            }
        }
    }
}
